import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send } from "lucide-react";
import { toast } from "sonner";
import { ASSETS } from "@/lib/brand";

const API = `${window.location.origin}/api`;

const STORAGE_KEY = "vdi_chat_session_v1";

const CANALI = ["Privato", "Ho.Re.Ca.", "Export"];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // {role, content}
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [streamed, setStreamed] = useState(""); // in-progress assistant text
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [cookieGate, setCookieGate] = useState(() => {
    try {
      return !localStorage.getItem("vdi_cookie_consent_v1");
    } catch {
      return false;
    }
  });
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  // Hide bubble while the cookie banner is on screen (mobile overlap)
  useEffect(() => {
    const check = () => {
      try {
        setCookieGate(!localStorage.getItem("vdi_cookie_consent_v1"));
      } catch {
        setCookieGate(false);
      }
    };
    window.addEventListener("vdi-cookie-decided", check);
    return () => window.removeEventListener("vdi-cookie-decided", check);
  }, []);

  // Open chat on global event (e.g. Sommelier Club CTA)
  useEffect(() => {
    const openChat = () => setOpen(true);
    window.addEventListener("vdi-open-chat", openChat);
    return () => window.removeEventListener("vdi-open-chat", openChat);
  }, []);

  // Restore session id from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.session_id) setSessionId(parsed.session_id);
        if (parsed.seen) setSeen(true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Persist session id
  useEffect(() => {
    if (!sessionId) return;
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ session_id: sessionId, seen: true })
      );
    } catch {
      /* ignore */
    }
  }, [sessionId]);

  // Fetch welcome message once
  useEffect(() => {
    if (!open || messages.length > 0) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/chat/welcome`);
        setMessages([{ role: "assistant", content: data.message }]);
      } catch {
        setMessages([
          {
            role: "assistant",
            content:
              "Benvenuto ne La Valle degli Iblei. In che modo posso servirla?",
          },
        ]);
      }
    })();
  }, [open, messages.length]);

  // Auto-scroll on new content
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streamed, showLeadForm]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setDraft("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    setStreamed("");

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) throw new Error("Bad response");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let assembled = "";
      let triggerForm = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Parse SSE events separated by blank line
        const parts = buffer.split(/\n\n/);
        buffer = parts.pop() || "";
        for (const raw of parts) {
          const lines = raw.split("\n");
          let event = "message";
          let dataStr = "";
          for (const line of lines) {
            if (line.startsWith("event:")) event = line.slice(6).trim();
            else if (line.startsWith("data:"))
              dataStr += line.slice(5).trim();
          }
          if (!dataStr) continue;
          let payload = {};
          try {
            payload = JSON.parse(dataStr);
          } catch {
            continue;
          }
          if (event === "session" && payload.session_id) {
            setSessionId(payload.session_id);
          } else if (event === "done") {
            triggerForm = !!payload.trigger_lead_form;
          } else if (event === "error") {
            throw new Error(payload.error || "Errore");
          } else if (payload.delta) {
            assembled += payload.delta;
            setStreamed(assembled);
          }
        }
      }

      // Commit assistant message
      setMessages((m) => [
        ...m,
        { role: "assistant", content: assembled.trim() },
      ]);
      setStreamed("");
      if (triggerForm) setShowLeadForm(true);
    } catch (err) {
      if (err.name !== "AbortError") {
        toast.error("Connessione interrotta. Riprovi tra un istante.");
        setStreamed("");
      }
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const suggestForm = () => setShowLeadForm(true);

  return (
    <>
      {/* Discreet lateral tab */}
      <AnimatePresence>
        {!open && !cookieGate && (
          <motion.button
            key="chat-tab"
            data-testid="chat-open-btn"
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 24 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            onClick={() => setOpen(true)}
            onMouseEnter={() => {
              if (window.matchMedia("(hover: hover)").matches) setOpen(true);
            }}
            className="fixed right-0 top-[58%] -translate-y-1/2 z-[70] flex flex-col items-center gap-2.5
                       py-4 px-2 bg-[#0A0A0A] text-[#FDFBF7]
                       border border-r-0 border-[#C4A671]/50 rounded-l-[4px]
                       shadow-[0_8px_30px_rgba(0,0,0,0.35)]
                       hover:border-[#C4A671] hover:pr-3 transition-all duration-300"
            aria-label="Apri il Concierge — Sommelier Club"
          >
            <span className="relative flex items-center justify-center w-7 h-7 shrink-0">
              <img
                src={ASSETS.logo}
                alt=""
                className="relative w-7 h-7 object-contain"
              />
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#C4A671] animate-pulse" />
            </span>
            <span
              className="font-serif-display italic text-[13px] text-[#F2EFE8]"
              style={{ writingMode: "vertical-rl", letterSpacing: "0.08em" }}
              data-testid="chat-open-label"
            >
              Il Concierge
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="chat-panel"
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
            className="fixed bottom-4 right-2 left-2 sm:left-auto sm:bottom-24 sm:right-6 z-[70] sm:w-[380px]
                       h-[calc(100dvh-120px)] sm:h-[580px] max-h-[calc(100dvh-60px)]
                       bg-[#FDFBF7] border border-[#C4A671]/40 shadow-2xl
                       flex flex-col overflow-hidden"
          >
            {/* Header */}
            <header className="flex items-center justify-between px-5 py-4 bg-[#0A0A0A] text-[#FDFBF7]">
              <div className="flex items-center gap-3">
                <img
                  src={ASSETS.logo}
                  alt=""
                  className="w-8 h-8 object-contain"
                />
                <div className="leading-tight">
                  <div
                    className="font-serif-display italic text-base"
                    style={{ letterSpacing: "0.02em" }}
                  >
                    Il Concierge
                  </div>
                  <div className="overline text-[8.5px] text-[#C4A671]">
                    Sommelier Club · Attivo 24/7
                  </div>
                </div>
              </div>
              <button
                data-testid="chat-close-btn"
                aria-label="Chiudi chat"
                onClick={() => setOpen(false)}
                className="p-1 hover:opacity-70 transition"
              >
                <X size={18} />
              </button>
            </header>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-5 py-5 space-y-4"
              data-testid="chat-messages"
            >
              {messages.map((m, i) => (
                <Bubble key={i} role={m.role} content={m.content} />
              ))}
              {streamed && <Bubble role="assistant" content={streamed} typing />}
              {sending && !streamed && <TypingDots />}
              {showLeadForm && (
                <LeadForm
                  sessionId={sessionId}
                  onSuccess={(name) => {
                    setShowLeadForm(false);
                    setMessages((m) => [
                      ...m,
                      {
                        role: "assistant",
                        content: `La Sua richiesta è stata presa in carico, ${name}. L'ufficio verificherà i riferimenti del locale e risponderà personalmente via email.\n\nNel frattempo resto a Sua completa disposizione: se desidera, posso raccontarLe la nostra filosofia, approfondire le cuvée d'autore o la Limited Edition Blanche, o suggerirLe gli abbinamenti della Maison.`,
                      },
                    ]);
                  }}
                  onCancel={() => setShowLeadForm(false)}
                />
              )}
            </div>

            {/* Composer — sempre attivo, anche mentre il lead form è aperto */}
            <div className="border-t border-[#EAEAEA] px-4 py-3 bg-white">
              <div className="flex items-center gap-2 mb-2">
                {!showLeadForm ? (
                  <button
                    onClick={suggestForm}
                    data-testid="chat-request-quote"
                    className="text-[10px] uppercase tracking-[0.22em] text-[#C4A671]
                               border border-[#C4A671]/50 px-3 py-1.5
                               hover:bg-[#C4A671] hover:text-[#0A0A0A] transition"
                  >
                    Richiedi Allocazione
                  </button>
                ) : (
                  <span
                    data-testid="chat-form-hint"
                    className="overline text-[9px] text-[#C4A671]"
                  >
                    Compili il modulo qui sopra · può continuare a scrivermi
                  </span>
                )}
              </div>
              <div className="flex items-end gap-2">
                <textarea
                  data-testid="chat-input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onKeyDown}
                  disabled={sending}
                  placeholder="Scriva il suo messaggio…"
                  rows={1}
                  className="flex-1 resize-none bg-transparent outline-none
                             font-serif-display text-[15px] text-[#0A0A0A]
                             placeholder:text-[#B4ADA2] placeholder:italic
                             disabled:opacity-50 border-b border-[#D6D2CA]
                             focus:border-[#0A0A0A] py-2 transition-colors"
                />
                <button
                  onClick={send}
                  disabled={sending || !draft.trim()}
                  data-testid="chat-send-btn"
                  aria-label="Invia messaggio"
                  className="w-9 h-9 flex items-center justify-center
                             bg-[#0A0A0A] text-[#FDFBF7] disabled:opacity-30
                             hover:bg-[#C4A671] hover:text-[#0A0A0A] transition"
                >
                  <Send size={14} />
                </button>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className="overline text-[8.5px] text-[#8B7A55]">
                  Powered by intelligenza · Made with Cura
                </span>
                <span className="overline text-[8.5px] text-[#8B7A55]">
                  {sessionId ? sessionId.slice(0, 6) : "·"}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Bubble({ role, content, typing }) {
  const isUser = role === "user";
  const hasListino = !isUser && typeof content === "string" && content.includes("[LISTINO_PDF]");
  const text = isUser || typeof content !== "string"
    ? content
    : content
        .replace(/\[B2B_LEAD\][\s\S]*?\[\/B2B_LEAD\]/g, "")
        .replaceAll("[LISTINO_PDF]", "")
        .replaceAll("[TRIGGER_LEAD_FORM]", "")
        .trim();
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        data-testid={isUser ? "chat-bubble-user" : "chat-bubble-assistant"}
        className={`max-w-[85%] px-4 py-3 leading-[1.6] whitespace-pre-wrap text-[14.5px] ${
          isUser
            ? "bg-[#0A0A0A] text-[#FDFBF7] font-light"
            : "bg-white border border-[#EAEAEA] text-[#1a1a1a] font-serif-display"
        }`}
        style={{
          borderRadius: 2,
          fontStyle: isUser ? "normal" : "italic",
        }}
      >
        {text}
        {hasListino && (
          <a
            href="/docs/Listino_Prezzi_2026_La_Valle_degli_Iblei.pdf"
            download
            target="_blank"
            rel="noopener noreferrer"
            data-testid="chat-listino-download"
            className="mt-3 not-italic flex items-center justify-center gap-2 border border-[#C4A671] text-[#8B7A55] hover:bg-[#C4A671] hover:text-[#0A0A0A] transition-colors px-4 py-2.5"
            style={{ fontFamily: "'Outfit', sans-serif", fontSize: "10px", letterSpacing: "0.22em", textTransform: "uppercase" }}
          >
            📄 Scarica il Listino Prezzi Ufficiale 2026
          </a>
        )}
        {typing && <span className="inline-block w-1 h-3 ml-1 bg-[#C4A671] animate-pulse" />}
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex justify-start" data-testid="chat-typing">
      <div className="px-4 py-3 bg-white border border-[#EAEAEA]">
        <div className="flex items-center gap-1.5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-[#C4A671] animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function LeadForm({ sessionId, onSuccess, onCancel }) {
  const [f, setF] = useState({
    nome: "",
    azienda: "",
    citta: "",
    canale: "Ho.Re.Ca.",
    email: "",
    telefono: "",
    messaggio: "",
  });
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (!f.azienda || !f.citta || !f.nome || !f.email) {
      toast.error("Ristorante/Azienda, città, referente ed email sono richiesti.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/sommelier-club`, { ...f, session_id: sessionId });
      toast.success("Richiesta trasmessa all'ufficio.");
      onSuccess(f.nome.split(" ")[0]);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Errore invio. Riprovi tra un istante."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="chat-lead-form"
      className="border border-[#C4A671]/60 bg-white p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <span className="overline text-[9.5px] text-[#C4A671]">
          Accesso Riservato
        </span>
        <button
          onClick={onCancel}
          className="overline text-[9px] text-[#5C5C5C] hover:text-[#0A0A0A]"
          data-testid="chat-lead-cancel"
        >
          Annulla
        </button>
      </div>
      <p className="font-serif-display italic text-[15px] text-[#0A0A0A] leading-snug">
        Lasci i riferimenti del suo locale — la richiesta sarà verificata
        dall&apos;ufficio, che risponderà personalmente via email.
      </p>

      <Input testid="lead-azienda" label="Nome del Ristorante / Azienda" value={f.azienda} onChange={upd("azienda")} required />
      <Input testid="lead-citta" label="Città" value={f.citta} onChange={upd("citta")} required />
      <Input testid="lead-nome" label="Nome e Cognome del Referente" value={f.nome} onChange={upd("nome")} required />
      <label className="block">
        <span className="overline text-[9px] block mb-1">Canale *</span>
        <select
          data-testid="lead-canale"
          className="w-full bg-transparent border-b border-[#D6D2CA] py-2 text-[14px] outline-none font-serif-display"
          value={f.canale}
          onChange={upd("canale")}
        >
          {CANALI.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </label>
      <Input testid="lead-email" label="Email" type="email" value={f.email} onChange={upd("email")} required />
      <Input testid="lead-telefono" label="Telefono (facoltativo)" value={f.telefono} onChange={upd("telefono")} />
      <label className="block">
        <span className="overline text-[9px] block mb-1">Note (bottiglie desiderate, volumi…)</span>
        <textarea
          data-testid="lead-messaggio"
          rows={2}
          className="w-full bg-transparent border-b border-[#D6D2CA] py-2 text-[14px] outline-none font-serif-display resize-none"
          value={f.messaggio}
          onChange={upd("messaggio")}
        />
      </label>
      <button
        data-testid="lead-submit"
        onClick={submit}
        disabled={busy}
        className="w-full bg-[#0A0A0A] text-[#FDFBF7] py-3 mt-2
                   text-[10.5px] font-medium tracking-[0.28em] uppercase
                   hover:bg-[#C4A671] hover:text-[#0A0A0A] transition disabled:opacity-50"
      >
        {busy ? "Invio in corso…" : "Trasmetti all'Ufficio →"}
      </button>
    </div>
  );
}

function Input({ label, testid, required, ...rest }) {
  return (
    <label className="block">
      <span className="overline text-[9px] block mb-1">
        {label} {required && <span className="text-[#63171B]">*</span>}
      </span>
      <input
        data-testid={testid}
        className="w-full bg-transparent border-b border-[#D6D2CA] py-2 text-[14px] outline-none font-serif-display focus:border-[#0A0A0A] transition-colors"
        {...rest}
      />
    </label>
  );
}
